// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc */
/* eslint-disable ta-code-conventions/has-matching-test */

import * as React from 'react';
import { Query } from 'urql';
import { localize } from '@ta/common.i18n';
import { isOwner } from '@ta/trips.trip-util';

import type { ListSize } from './ListItem';
import ListItem from './ListItem';
import PoiDetails from './PoiDetails.gql';
import ReviewDetails from './ReviewDetails.gql';
import PhotoDetails from './PhotoDetails.gql';
import VideoDetails from './VideoDetails.gql';
import TripDetails from './TripDetails.gql';

type ShareViaEmailProps = {
  url: string,
  subject: string,
  firstSentence: string,
  textBeforeLink: string,
  textAfterLink: string,
  size: ListSize,
};

type ExternalProps = {
  url: string,
  itemType: string,
  itemId: number,
  size: 'small' | 'large',
};

const locationItemTypes: string[] = [
  'Restaurant_Review',
  'Attraction_Review',
  'MobileAttraction_Review',
  'AttractionProductReview',
  'MobileAttractionProductReview',
  'Hotel_Review',
  'MobileHotel_Review',
];

const shareViaEmail = (
  url: string,
  subjectOverride: string,
  firstSentenceOverride: string,
  textBeforeLink: string,
  textAfterLink: string,
) => {
  const urlEscaped = encodeURIComponent(url);
  const textBeforeLinkEscaped = encodeURIComponent(textBeforeLink);
  const textAfterLinkEscaped = encodeURIComponent(textAfterLink);
  const subject = encodeURIComponent(subjectOverride || localize('share_email_subject'));
  const firstSentence = firstSentenceOverride || localize('link_share_sms_checkthisout');

  const to = '';
  const body = `${firstSentence}%0D%0A%0D%0A${
    textBeforeLinkEscaped ? `${textBeforeLinkEscaped}%0D%0A` : ``
  }${urlEscaped}%0D%0A${
    textAfterLinkEscaped ? `${textAfterLinkEscaped}%0D%0A` : ''
  }%0D%0A${localize('ib_share_booking_download_app')}%0D%0A${localize('app_download_url_secure')}`;

  // eslint-disable-next-line no-restricted-globals
  window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
};

const ShareViaEmail = ({
  url,
  subject,
  firstSentence,
  textBeforeLink,
  textAfterLink,
  size,
}: ShareViaEmailProps) => (
  <ListItem
    iconName="email-fill"
    localizedText={localize('link_share_option_email')}
    size={size}
    onClickFunction={() =>
      shareViaEmail(url, subject, firstSentence, textBeforeLink, textAfterLink)
    }
  />
);

ShareViaEmail.defaultProps = {
  firstSentence: '',
  subject: '',
  textBeforeLink: '',
  textAfterLink: '',
};

const ShareViaEmailWithData = (externalProps: ExternalProps) => {
  if (locationItemTypes.indexOf(externalProps.itemType) > -1) {
    return (
      <Query query={PoiDetails} variables={{ locationId: externalProps.itemId }}>
        {({ fetching, data }) => {
          if (fetching || !data) {
            return <ShareViaEmail {...externalProps} />;
          }
          const locationName = data.locations[0] ? data.locations[0].name || '' : '';
          return (
            <ShareViaEmail
              {...externalProps}
              subject={localize('link_share_email_subject', { '0': locationName })}
              firstSentence={localize('mobile_here_is_a_place_i_found_8e0')}
              textBeforeLink={locationName}
            />
          );
        }}
      </Query>
    );
  } else if (externalProps.itemType === 'Review') {
    return (
      <Query query={ReviewDetails} variables={{ reviewId: externalProps.itemId }}>
        {({ fetching, data }) =>
          fetching || !data ? (
            <ShareViaEmail {...externalProps} />
          ) : (
            <ShareViaEmail
              {...externalProps}
              firstSentence={localize('share_email_reviews')}
              textBeforeLink={data.reviews[0] ? data.reviews[0].title || '' : ''}
            />
          )
        }
      </Query>
    );
  } else if (externalProps.itemType === 'Photo') {
    return (
      <Query query={PhotoDetails} variables={{ id: externalProps.itemId }}>
        {({ fetching, data }) =>
          fetching || !data ? (
            <ShareViaEmail {...externalProps} />
          ) : (
            <ShareViaEmail
              {...externalProps}
              firstSentence={localize('share_email_photos')}
              textBeforeLink={data.photos[0] ? data.photos[0].caption || '' : ''}
            />
          )
        }
      </Query>
    );
  } else if (externalProps.itemType === 'Video') {
    return (
      <Query query={VideoDetails} variables={{ id: externalProps.itemId }}>
        {({ fetching, data }) =>
          fetching || !data ? (
            <ShareViaEmail {...externalProps} />
          ) : (
            <ShareViaEmail
              {...externalProps}
              firstSentence={localize('share_email_videos')}
              textBeforeLink={data.videos[0] ? data.videos[0].caption || '' : ''}
            />
          )
        }
      </Query>
    );
  } else if (externalProps.itemType === 'LinkPost') {
    return (
      <ShareViaEmail
        {...externalProps}
        subject={localize('share_email_link_subject')}
        firstSentence={localize('share_email_link')}
        textAfterLink={localize('share_email_link_attribution')}
      />
    );
  } else if (externalProps.itemType === 'ForumPost') {
    return <ShareViaEmail {...externalProps} firstSentence={localize('share_email_forumtopic')} />;
  } else if (externalProps.itemType === 'ForumResponse') {
    return <ShareViaEmail {...externalProps} firstSentence={localize('share_email_forumreply')} />;
  } else if (externalProps.itemType === 'WriteTrip' || externalProps.itemType === 'ReadTrip') {
    const writeTrip = externalProps.itemType === 'WriteTrip';
    return (
      <Query query={TripDetails} variables={{ id: externalProps.itemId }}>
        {({ fetching, data }) => {
          let shareEmailProps = externalProps;
          if (!fetching && data) {
            // when coming from Trips home the array will contain all Trips, not only the one requested
            const trip = data.trips && data.trips.find(t => t.id === externalProps.itemId);
            const bodyParams = {
              tripName: (trip && trip.title) || '',
            };
            const isMine = isOwner(trip);
            shareEmailProps = Object.assign({}, externalProps, {
              firstSentence: localize('trips_share_email_hey'), // Hey,
              subject: localize('trips_share_email_subject'), // Check out my Trip on TripAdvisor!
              textBeforeLink: localize('trips_share_email_body_v2', bodyParams), // I created a Trip on TripAdvisor! Check out \"{tripName}\"!
            });

            if (writeTrip) {
              shareEmailProps.subject = localize('trips_collaborate_invite_email_subject'); // Join my Trip on TripAdvisor!
              if (isMine) {
                shareEmailProps.textBeforeLink = localize(
                  'trips_collaborate_invite_email_body_v2',
                  bodyParams,
                ); // I created a Trip on TripAdvisor! Join and add your ideas to \"{tripName}\"!
              } else {
                shareEmailProps.textBeforeLink = localize(
                  'trips_collaborate_invite_email_body_collaborator_version',
                  bodyParams,
                ); // Join and add your ideas to \"{tripName}\"!
              }
            } else if (!isMine) {
              shareEmailProps.subject = localize('trips_share_email_subject');
              shareEmailProps.textBeforeLink = localize(
                'trips_email_share_collaborator_version',
                bodyParams,
              ); // Check out \"{tripName}\"!
            }
          }
          return <ShareViaEmail {...shareEmailProps} />;
        }}
      </Query>
    );
  }
  return <ShareViaEmail {...externalProps} />;
};

export default ShareViaEmailWithData;
